/**
 * Vuetify 3 Validation Rules
 * Replaces vee-validate custom validators
 */

/**
 * Validates Brazilian CNPJ (Cadastro Nacional de Pessoa Jurídica)
 */
function validateCnpj(s: string | null | undefined): boolean {
  if (!s) return false;

  const cnpj = s.replace(/[^\d]+/g, "");

  // Valida a quantidade de caracteres
  if (cnpj.length !== 14) return false;

  // Elimina inválidos com todos os caracteres iguais
  if (/^(\d)\1+$/.test(cnpj)) return false;

  // Cáculo de validação
  const t = cnpj.length - 2,
    d = cnpj.substring(t),
    d1 = parseInt(d.charAt(0)),
    d2 = parseInt(d.charAt(1)),
    calc = (x: number) => {
      const n = cnpj.substring(0, x);
      let y = x - 7;
      let s = 0;
      let r = 0;

      for (let i = x; i >= 1; i--) {
        s += parseInt(n.charAt(x - i)) * y--;
        if (y < 2) y = 9;
      }

      r = 11 - (s % 11);
      return r > 9 ? 0 : r;
    };

  return calc(t) === d1 && calc(t + 1) === d2;
}

/**
 * Password validation regex
 * At least 8 characters, containing: Numbers, uppercase and lowercase letters, and special characters
 */
const PASSWORD_REGEXP =
  /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/;

/**
 * Validation rules for Vuetify 3 forms
 */
export const validationRules = {
  /**
   * Required field validation
   */
  required: (value: any) => {
    if (value === null || value === undefined || value === '') {
      return 'Este campo é obrigatório';
    }
    if (Array.isArray(value) && value.length === 0) {
      return 'Este campo é obrigatório';
    }
    return true;
  },

  /**
   * Email validation
   */
  email: (value: string | null | undefined) => {
    if (!value) return true; // Allow empty, use required rule separately
    const pattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return pattern.test(value) || 'E-mail inválido';
  },

  /**
   * CNPJ validation
   */
  cnpj: (value: string | null | undefined) => {
    if (!value) return true; // Allow empty, use required rule separately
    return validateCnpj(value) || 'CNPJ inválido';
  },

  /**
   * Password validation
   * At least 8 characters, containing: Numbers, uppercase and lowercase letters, and special characters
   */
  password: (value: string | null | undefined) => {
    if (!value) return true; // Allow empty, use required rule separately
    return (
      PASSWORD_REGEXP.test(value) ||
      'Pelo menos 8 caracteres, contendo: Números, letras maiúsculas e minúsculas e caracteres especiais'
    );
  },

  /**
   * Minimum length validation
   */
  minLength: (min: number) => (value: string | null | undefined) => {
    if (!value) return true; // Allow empty, use required rule separately
    return value.length >= min || `Mínimo de ${min} caracteres`;
  },

  /**
   * Maximum length validation
   */
  maxLength: (max: number) => (value: string | null | undefined) => {
    if (!value) return true; // Allow empty, use required rule separately
    return value.length <= max || `Máximo de ${max} caracteres`;
  },

  /**
   * Numeric validation
   */
  numeric: (value: string | number | null | undefined) => {
    if (!value && value !== 0) return true; // Allow empty, use required rule separately
    const pattern = /^[0-9]+$/;
    return pattern.test(String(value)) || 'Apenas números são permitidos';
  },

  /**
   * Min value validation
   */
  min: (minValue: number) => (value: number | null | undefined) => {
    if (value === null || value === undefined) return true;
    return value >= minValue || `Valor mínimo: ${minValue}`;
  },

  /**
   * Max value validation
   */
  max: (maxValue: number) => (value: number | null | undefined) => {
    if (value === null || value === undefined) return true;
    return value <= maxValue || `Valor máximo: ${maxValue}`;
  },

  /**
   * Confirm password/field match validation
   */
  confirmed: (targetValue: any) => (value: any) => {
    return value === targetValue || 'Os campos não coincidem';
  },
};
